import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Lock, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PasswordRequirements } from "@/components/PasswordRequirements";
import { PasswordInput } from "@/components/PasswordInput";
import { isPasswordValid, PASSWORD_POLICY_MSG } from "@/lib/password";

const LOGO = "/org_logo.png";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) { setError("Missing or invalid reset link."); return; }
    if (!isPasswordValid(newPw)) { setError(PASSWORD_POLICY_MSG); return; }
    if (newPw !== confirmPw) { setError("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: newPw });
      toast.success("Password reset. Please sign in.");
      navigate("/login");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center px-6 py-10 bg-background">
      <div className="absolute top-6 right-6"><ThemeToggle /></div>
      <div className="mx-auto w-full max-w-sm animate-fade-up">
        <Link to="/" data-testid="reset-logo-home-link" aria-label="MELPWU home" className="block w-fit mx-auto mb-8"><img src={LOGO} alt="MELPWU logo" className="h-28 w-28 rounded-xl bg-white p-2 object-contain border border-border block" /></Link>
        <span className="text-xs uppercase tracking-[0.2em] text-primary">Account recovery</span>
        <h2 className="font-display text-3xl font-bold tracking-tight mt-1">Set a new password</h2>
        <p className="text-sm text-muted-foreground mt-2">Choose a new password. It must be different from your SSNIT number.</p>
        <form onSubmit={submit} className="mt-8 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="newPw">New password</Label>
            <PasswordInput leftIcon id="newPw" testId="reset-new-password" value={newPw}
              onChange={(e) => setNewPw(e.target.value)} placeholder="Create a strong password" required />
            <PasswordRequirements value={newPw} className="pt-1" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirmPw">Confirm new password</Label>
            <PasswordInput leftIcon id="confirmPw" testId="reset-confirm-password" value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)} placeholder="Re-enter password" required />
          </div>
          {error && (
            <div data-testid="reset-error" className="flex items-start gap-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <Warning size={18} className="mt-0.5 shrink-0" /> {error}
            </div>
          )}
          <Button type="submit" data-testid="reset-submit-button" disabled={busy} className="w-full h-11 text-base">
            {busy ? "Saving…" : "Reset password"}
          </Button>
        </form>
        <button type="button" onClick={() => navigate("/login")}
          className="mt-6 block w-full text-center text-sm text-muted-foreground hover:text-primary transition-colors">
          Back to sign in
        </button>
      </div>
    </div>
  );
}
